/**
 * Nota de credito sobre una venta.
 *
 * ANULAR NO ES ACREDITAR
 * ----------------------
 * Anular es "esta venta nunca existio" y solo sirve mientras no paso nada: sin
 * cobros, sin devoluciones. La nota de credito es "esta venta existio y se
 * corrige": el cliente devolvio un equipo, se cobro de mas, se cambia una parte.
 * Emite un comprobante nuevo con su numero, y la venta original queda intacta
 * porque fue real. El backend tenia la ruta desde el principio; lo que faltaba
 * era la pantalla.
 *
 * TOTAL O POR ITEM
 * ----------------
 * Lo mas comun es devolver un solo item de una venta de varios, asi que el
 * dialogo muestra las lineas y arranca con todas marcadas. Se destildan las que
 * no vuelven. Si quedan todas, se manda la NC completa sin `items`: asi la base
 * copia tambien el descuento general de la venta, que por linea no se ve.
 *
 * DE DONDE SALE LA PLATA
 * ----------------------
 * Con cliente, el credito va a su cuenta corriente y no se pregunta nada. Sin
 * cliente no hay donde acreditar: la plata vuelve por la caja, y hay que decir
 * por cual. Se propone la caja de la moneda de la venta, que es por donde entro
 * casi siempre.
 *
 * Sobre `ui/dialog` (Radix): Escape cierra, el foco queda atrapado adentro y
 * vuelve al boton que lo abrio.
 */
import { useMemo, useState } from "react";
import { Undo2 } from "lucide-react";
import { parsearImporte, type Moneda } from "@tora/shared";

import { cn } from "@/lib/utils";
import {
  aNumero,
  type CuentaFinanciera,
  type DetalleComprobante,
  type NuevaNotaCredito,
} from "@/lib/tipos";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { Input } from "@/components/ui/input";
import { SelectNativo } from "@/components/ui/select-nativo";
import { Importe } from "@/components/money/importe";

/** Lo que el dialogo necesita saber de la venta que se acredita. */
export interface VentaAAcreditar {
  id: number;
  numero: string;
  moneda: Moneda;
  total: number;
  cliente_id: number | null;
}

/** Cantidad a devolver por `detalle_id`. Lo que no esta, no vuelve. */
export type Cantidades = Record<number, number>;

/**
 * Lo que queda por devolver de una linea: lo vendido menos lo que ya volvio
 * por otras notas de credito. Antes el dialogo proponia siempre lo vendido, y
 * una venta ya devuelta se ofrecia entera otra vez hasta que la base la frenaba.
 */
export function quedaPorDevolver(d: DetalleComprobante): number {
  return Math.max(0, aNumero(d.cantidad) - aNumero(d.cantidad_acreditada ?? 0));
}

/** Suma de lo que se acredita, linea por linea, proporcional a la cantidad. */
export function totalAcreditar(detalle: DetalleComprobante[], cantidades: Cantidades): number {
  let total = 0;
  for (const d of detalle) {
    const vendida = aNumero(d.cantidad);
    const vuelve = cantidades[d.id] ?? 0;
    if (vuelve <= 0 || vendida <= 0) continue;
    total += (aNumero(d.importe) * Math.min(vuelve, vendida)) / vendida;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Cuanto de cada peso de lista se cobro de verdad: el total de la venta sobre
 * la suma de sus lineas. Con un descuento general la NC por lineas lleva su
 * parte (0121_1); sin esto el dialogo prometia $ 9.000 de una venta de
 * $ 9.000 + $ 1.000 cobrada $ 5.000, y la base acreditaba $ 4.500.
 */
export function factorDescuento(detalle: DetalleComprobante[], totalVenta: number): number {
  const lineas = detalle.reduce((acc, d) => acc + aNumero(d.importe), 0);
  return lineas > 0 && totalVenta < lineas ? totalVenta / lineas : 1;
}

/**
 * Si vuelve todo, es una NC completa: sin `items`, la base copia el descuento
 * general. Con algo ya devuelto nunca es completa: la base no acredita entera
 * una venta que ya tiene NC, y lo que queda se manda linea por linea.
 */
export function esCompleta(detalle: DetalleComprobante[], cantidades: Cantidades): boolean {
  return (
    detalle.length > 0 &&
    detalle.every(
      (d) =>
        aNumero(d.cantidad_acreditada ?? 0) <= 0 && (cantidades[d.id] ?? 0) >= aNumero(d.cantidad),
    )
  );
}

/**
 * Arma el cuerpo de la NC. Devuelve `null` si no hay nada que acreditar.
 *
 * `cuenta_id` solo viaja cuando la venta no tiene cliente: con cliente la base
 * no lo mira, y mandarlo igual seria decir algo que no es.
 */
export function armarNotaCredito(args: {
  detalle: DetalleComprobante[];
  cantidades: Cantidades;
  motivo: string;
  cuentaId: number | undefined;
  sinCliente: boolean;
}): NuevaNotaCredito | null {
  const { detalle, cantidades, motivo, cuentaId, sinCliente } = args;
  const lineas = detalle
    .map((d) => ({
      // `comprobante_detalle.id` es bigint: llega de la API como texto ("118")
      // aunque el tipo diga number. Mandado asi, la NC por lineas volvia
      // «Expected number, received string» y lo que quedaba de una venta ya
      // devuelta en parte no se podia acreditar nunca.
      detalle_id: Number(d.id),
      cantidad: Math.min(cantidades[d.id] ?? 0, quedaPorDevolver(d)),
    }))
    .filter((l) => l.cantidad > 0);
  if (lineas.length === 0) return null;

  const datos: NuevaNotaCredito = {};
  const texto = motivo.trim();
  if (texto) datos.motivo = texto;
  if (!esCompleta(detalle, cantidades)) datos.items = lineas;
  if (sinCliente && cuentaId !== undefined) datos.cuenta_id = cuentaId;
  return datos;
}

/** La caja por donde entro casi seguro: efectivo de la moneda de la venta. */
export function cajaPorDefecto(cuentas: CuentaFinanciera[], moneda: Moneda): number | undefined {
  const activas = cuentas.filter((c) => c.activo);
  return (
    activas.find((c) => c.tipo === "CAJA" && c.moneda === moneda) ??
    activas.find((c) => c.moneda === moneda) ??
    activas[0]
  )?.id;
}

function cantidadesIniciales(detalle: DetalleComprobante[]): Cantidades {
  const c: Cantidades = {};
  for (const d of detalle) c[d.id] = quedaPorDevolver(d);
  return c;
}

/**
 * Cuantas unidades vuelven, leido del campo de texto. Es una cantidad, no
 * plata: un campo vacio o ilegible es "no vuelve nada de esta linea" (0), que
 * es exactamente lo que significa destildarla. Nunca pasa de lo vendido.
 */
function cantidadDesde(texto: string, vendida: number): number {
  const n = parsearImporte(texto);
  if (n === null) return 0;
  return Math.min(vendida, n);
}

export function DialogNotaCredito({
  venta,
  detalle,
  cargando,
  cuentas,
  guardando,
  onCerrar,
  onConfirmar,
}: {
  venta: VentaAAcreditar;
  detalle: DetalleComprobante[];
  cargando: boolean;
  cuentas: CuentaFinanciera[];
  guardando: boolean;
  onCerrar: () => void;
  onConfirmar: (datos: NuevaNotaCredito) => void;
}): JSX.Element {
  const sinCliente = venta.cliente_id === null;
  const [motivo, setMotivo] = useState("");
  const [tocadas, setTocadas] = useState<Cantidades | null>(null);
  const [cuentaElegida, setCuentaElegida] = useState<number | undefined>(undefined);

  // Las cantidades arrancan en "vuelve todo" apenas llega el detalle, sin un
  // efecto de por medio: hasta que el usuario toca algo, se derivan.
  const cantidades = tocadas ?? cantidadesIniciales(detalle);
  const cuentaId = cuentaElegida ?? cajaPorDefecto(cuentas, venta.moneda);

  const completa = esCompleta(detalle, cantidades);
  // Todo lo vendido ya volvio: no hay nada que acreditar.
  const yaDevuelta =
    !cargando && detalle.length > 0 && detalle.every((d) => quedaPorDevolver(d) <= 0);
  const factor = factorDescuento(detalle, venta.total);
  const total = completa
    ? venta.total
    : Math.round(totalAcreditar(detalle, cantidades) * factor * 100) / 100;
  const datos = useMemo(
    () => armarNotaCredito({ detalle, cantidades, motivo, cuentaId, sinCliente }),
    [detalle, cantidades, motivo, cuentaId, sinCliente],
  );
  const cuenta = cuentas.find((c) => c.id === cuentaId);
  const faltaCaja = sinCliente && cuentaId === undefined;
  const puedeConfirmar = datos !== null && !faltaCaja && !guardando && !cargando;

  const fijar = (id: number, valor: number) =>
    setTocadas({ ...cantidades, [id]: Math.max(0, valor) });

  return (
    <Dialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !guardando) onCerrar();
      }}
    >
      <DialogContent tamano="lg">
        <DialogHeader>
          <DialogTitle>Nota de crédito de {venta.numero}</DialogTitle>
          <DialogDescription>
            La venta queda como está. Lo que se marca vuelve al stock y se acredita en un
            comprobante nuevo.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* Las lineas */}
          <Card padding="none" className="overflow-hidden">
            {cargando ? (
              <Cargando texto="Cargando el detalle…" className="py-6" />
            ) : detalle.length === 0 ? (
              <EstadoVacio tamano="sm" titulo="Esta venta no tiene líneas." />
            ) : (
              <ul className="divide-y divide-border">
                {detalle.map((d) => {
                  const vendida = aNumero(d.cantidad);
                  const queda = quedaPorDevolver(d);
                  const devuelta = vendida - queda;
                  const vuelve = cantidades[d.id] ?? 0;
                  const marcada = vuelve > 0;
                  return (
                    <li
                      key={d.id}
                      className={cn("flex items-center gap-3 px-3 py-2", !marcada && "opacity-60")}
                    >
                      <Checkbox
                        checked={marcada}
                        disabled={queda <= 0}
                        onChange={(e) => fijar(d.id, e.target.checked ? queda : 0)}
                        aria-label={`Devolver ${d.descripcion}`}
                        className="min-w-0 flex-1"
                        label={
                          <>
                            <span className="block truncate">{d.descripcion}</span>
                            <span className="block text-xs font-normal text-muted-foreground">
                              {d.serie_imei ? (
                                <>
                                  IMEI <Codigo>{d.serie_imei}</Codigo>
                                </>
                              ) : (
                                <>
                                  {vendida} ×{" "}
                                  <Importe
                                    monto={aNumero(d.precio_unitario)}
                                    moneda={venta.moneda}
                                    tamano="sm"
                                    codigo={false}
                                  />
                                </>
                              )}
                              {devuelta > 0 &&
                                (queda <= 0 ? " · Ya devuelta" : ` · Ya volvieron ${devuelta}`)}
                            </span>
                          </>
                        }
                      />
                      {/* Texto y no `type="number"`: la rueda del mouse no cambia la
                          cantidad a devolver por accidente. */}
                      {queda > 1 && (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={vuelve}
                          onChange={(e) => fijar(d.id, cantidadDesde(e.target.value, queda))}
                          aria-label={`Cantidad a devolver de ${d.descripcion}`}
                          className="w-16 shrink-0 text-right tabular-nums"
                        />
                      )}
                      <Importe
                        monto={
                          marcada ? (aNumero(d.importe) * Math.min(vuelve, vendida)) / vendida : 0
                        }
                        moneda={venta.moneda}
                        className="w-28 shrink-0 justify-end"
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {yaDevuelta && (
            <Aviso tono="warning" compacto>
              Esta venta ya se devolvió completa: no queda nada por acreditar.
            </Aviso>
          )}

          <div className="flex items-baseline justify-between gap-4 px-1">
            <span className="text-xs text-muted-foreground">
              {completa
                ? "Se acredita la venta completa"
                : factor < 1
                  ? "Se acreditan las líneas marcadas, con el descuento de la venta"
                  : "Se acreditan las líneas marcadas"}
            </span>
            {/* Negativo con el signo de Intl: es plata que sale. */}
            <Importe
              monto={total === 0 ? 0 : -total}
              moneda={venta.moneda}
              tamano="lg"
              className="font-semibold"
            />
          </div>

          {/* A donde va la plata */}
          {sinCliente ? (
            <Campo
              label="La plata sale de"
              ayuda={`La venta no tiene cliente, así que no hay cuenta corriente donde acreditar: la devolución queda como un egreso de esa caja${
                cuenta && cuenta.moneda !== venta.moneda
                  ? `, convertido a ${cuenta.moneda} a la cotización de la venta`
                  : ""
              }.`}
            >
              <SelectNativo
                value={cuentaId ?? ""}
                onChange={(e) => setCuentaElegida(Number(e.target.value) || undefined)}
              >
                {cuentaId === undefined && <option value="">Elegí una caja</option>}
                {cuentas
                  .filter((c) => c.activo)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} · {c.moneda}
                    </option>
                  ))}
              </SelectNativo>
            </Campo>
          ) : (
            <Aviso tono="neutral" compacto>
              El crédito va a la cuenta corriente del cliente: cancela lo que deba, y si no debe
              nada le queda saldo a favor.
            </Aviso>
          )}

          <Campo label="Motivo">
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por qué vuelve"
            />
          </Campo>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!puedeConfirmar}
            onClick={() => datos && onConfirmar(datos)}
          >
            <Undo2 aria-hidden="true" />
            Emitir nota de crédito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
